/**
 * Utility functions for model configuration
 * Generates provider settings from centralized model config
 */

import { AI_MODELS, DEFAULT_MODELS } from '$lib/config/aiModels.config'
import type { AIProvider } from '$lib/stores/settings'

/**
 * Generate AI provider configurations from model config
 */
export function generateAIProviders(): AIProvider[] {
  const providers: AIProvider[] = []
  
  // OpenRouter is always first and hidden
  providers.push({
    id: 'openrouter',
    name: 'OpenRouter Proxy',
    icon: '🌐',
    enabled: true,  // Always enabled
    apiKey: '',  // No API key needed for proxy
    models: ['openai/gpt-oss-120b', 'openai/qwen3-32b'],
    defaultModel: 'openai/gpt-oss-120b',
    hidden: true  // Hide from UI settings
  })
  
  // Generate providers from AI_MODELS
  const providerConfigs: Record<string, { name: string; icon: string }> = {
    openai: { name: 'OpenAI', icon: '' },
    anthropic: { name: 'Anthropic', icon: '' },
    google: { name: 'Google AI', icon: '' },
    upstage: { name: 'Upstage', icon: '' }
  }
  
  Object.entries(AI_MODELS).forEach(([providerId, models]) => {
    // Skip openrouter as it's already added
    if (providerId === 'openrouter') return
    
    const config = providerConfigs[providerId]
    if (!config) return
    
    providers.push({
      id: providerId,
      name: config.name,
      icon: config.icon,
      enabled: false,
      apiKey: '',
      models: models.map(m => m.id),
      defaultModel: DEFAULT_MODELS[providerId] || models[0]?.id
    })
  })
  
  return providers
}

/**
 * Get model list for a specific provider
 */
export function getProviderModels(providerId: string): string[] {
  const models = AI_MODELS[providerId]
  return models ? models.map(m => m.id) : []
}

/**
 * Validate if a model ID exists for a provider
 */
export function isValidModel(providerId: string, modelId: string): boolean {
  const models = AI_MODELS[providerId]
  return models ? models.some(m => m.id === modelId) : false
}